import JobQueueSubscriber from '@/controllers/JobQueueSubscriber';
import * as M from '@/model';
import mixins from '@/plugins/typed-mixins';
import api from '@/rest-api';
import { RouteNames } from '@/router/route-names';
import { sortBy } from '@/util/array-util';
import DocumentResultView from '@/views/DocumentResultView.vue';
import Vue, { VNode } from 'vue';

export default mixins(JobQueueSubscriber).extend({
  name: 'DocumentResultController',
  components: {
    DocumentResultView,
  },

  props: {
    documentId: { type: Number, required: true },
    ingestId: { type: Number as () => number | undefined, required: false },
    scrollToFragmentId: { type: Number as () => number | undefined, required: false },
  },

  data() {
    return {
      loading: false,
      document: undefined as M.FileDocument | undefined,
      descriptors: [] as M.IngestDescriptor[],
      currentDescriptor: undefined as M.IngestDescriptor | undefined,
      result: undefined as M.IngestResult | undefined,
      fragments: [] as M.IngestFragment[],
      suggestions: [] as M.Suggestion[],
      extractedEntities: [] as M.ExtractedEntity[],
      entityTypes: [] as M.EntityType[],
    };
  },

  mounted() {
    this.load();
  },

  watch: {
    ingestId(ingestId?: number) {
      if (ingestId) {
        this.load();
      }
    },
  },

  methods: {
    async load(force = false) {
      this.loading = true;
      const descriptors = sortBy(
        (d: M.IngestDescriptor) => d.job?.created || d.ingestId || 0, true,
      )(await api.ingestDescriptors(this.documentId));
      if (!this.ingestId) {
        const head = descriptors[0];
        if (head?.ingestId) {
          this.changeIngestion(head);
          return;
        }
      }
      if (this.documentId !== this.document?.id || force) {
        // only load document when necessary, we dont want to rerender the document view and lose user scroll state etc.
        this.document = await api.document(this.documentId);
      }
      if (this.ingestId) {
        this.result = await api.ingestResult(this.documentId, this.ingestId);
        this.fragments = await api.ingestFragments(this.ingestId);
        this.suggestions = await api.ingestSuggestions(this.ingestId);
        this.suggestions.sort((a, b) => a.boxId - b.boxId);
        this.extractedEntities = await api.extractedEntities(this.ingestId);
        if (this.entityTypes.length === 0) {
          this.entityTypes = await api.entityTypes();
        }
      }
      this.descriptors = this.subscribe(descriptors, 'job');
      this.currentDescriptor = this.result?.descriptor || this.descriptors[0];
      if (!this.currentDescriptor.job?.stopped) {
        this.subscribeUntil((job) => job.id === this.currentDescriptor?.job?.id && !!job.stopped)
          .then((stoppedJob) => {
            if (stoppedJob.status === 'complete') {
              this.descriptorFromJob(this.documentId, stoppedJob.id)
                .then((desc) => {
                  if (desc) {
                    this.currentDescriptor = desc;
                    this.changeIngestion(desc);
                  }
                });
            }
          });
      }
      this.loading = false;
    },
    changeIngestion(descriptor: M.IngestDescriptor) {
      const goto = (ingestId: number) => {
        if (this.ingestId === ingestId) {
          return;
        }
        if (this.$router.currentRoute.name !== RouteNames.DocumentResult) {
          this.$emit('changeIngestion', descriptor);
        } else {
          const params = { documentId: `${this.documentId}`, ingestId: `${ingestId}` };
          if (this.ingestId) {
            this.$router.push({ name: RouteNames.DocumentResult, params });
          } else {
            this.$router.replace({ name: RouteNames.DocumentResult, params });
          }
        }
      };
      if (descriptor.ingestId) {
        goto(descriptor.ingestId);
      } else if (descriptor.job) {
        this.descriptorFromJob(this.documentId, descriptor.job?.id)
          .then((desc) => {
            if (desc?.ingestId) {
              goto(desc.ingestId);
            }
          });
      }
    },
    descriptorFromJob(documentId: number, jobId: number): Promise<M.IngestDescriptor | undefined> {
      return api.ingestDescriptors(documentId)
        .then((ds) => ds.find((d) => d.job?.id === jobId));
    },
    onSaveFragment(fragment: M.IngestFragment, suggestion?: M.Suggestion) {
      this.saveFragment(fragment, suggestion);
    },
    onDeleteFragment(id: number) {
      api.deleteFragment(id).then(() => {
        this.$emit('fragmentDeleted', id);
        const index = this.fragments.findIndex((f) => f.id === id);
        Vue.delete(this.fragments, index);
        // if any suggestion is linked to this fragment, remove the link
        const i = this.suggestions.findIndex((s) => s.action?.fragmentId === id);
        if (i > -1) {
          Vue.set(this.suggestions, i, { ...this.suggestions[i], action: undefined });
        }
      });
    },
    async onRevertFragment(id: number) {
      if (this.ingestId) {
        // Could be changed to only fetch the relevant fragment
        const persitedFragments = await api.ingestFragments(this.ingestId);
        const original = persitedFragments.find((f) => f.id === id);

        const index = this.fragments.findIndex((f) => f.id === id);
        if (index > -1) {
          Vue.set(this.fragments, index, original);
        }
      }
    },
    async onSaveLabel(editedLabels: M.Label[]) {
      const savedLabels = await Promise.all(editedLabels.map((l) => (l.id < 1 ? api.saveLabel(l) : Promise.resolve(l))));
      await api.saveDocument({ ...this.document, labels: savedLabels.map((l) => l.id) });
      this.load();
    },
    async createJob(manifest: M.SerfJobManifest) {
      await api.createJob(manifest);
      this.load();
    },
    async saveSuggestion(suggestion: M.Suggestion) {
      const savedSuggestion = await api.saveSuggestion(suggestion);
      const index = this.suggestions.findIndex((s) => s.id === savedSuggestion.id);
      if (index > -1) {
        Vue.set(this.suggestions, index, savedSuggestion);
      } else {
        this.suggestions.push(savedSuggestion);
      }
    },

    async saveFragment(fragment: M.IngestFragment, suggestion?: M.Suggestion) {
      const saved = await api.saveFragment(fragment);
      if (suggestion && M.isIngestTextFragment(saved)) {
        // check if fragment and suggestion overlap
        if (saved.boxes.some((b) => b.boxId === suggestion.boxId && suggestion.from <= b.from && suggestion.to >= b.to)) {
          await this.saveSuggestionAction(suggestion, saved);
        }
      }
      this.$emit('fragmentSaved', saved);
      const index = this.fragments.findIndex((f) => f.id === fragment.id);
      if (index > -1) {
        Vue.set(this.fragments, index, saved);
      } else {
        this.fragments.push(saved);
      }
      return saved;
    },
    async saveSuggestionAction(suggestion: M.Suggestion, fragment?: M.IngestFragment) {
      await this.saveSuggestion({
        ...suggestion,
        action: {
          fragmentId: fragment?.id,
        },
      });
    },
    async removeAction(suggestion: M.Suggestion) {
      await this.saveSuggestion({
        ...suggestion,
        action: undefined,
      });
    },
    updateDocument(document: M.FileDocument) {
      this.document = document;
    },
    fragmentFromSuggestion(suggestion: M.Suggestion): M.IngestTextFragment {
      return {
        id: 0,
        type: 'text',
        boxes: [
          {
            from: suggestion.from,
            to: suggestion.to,
            boxId: suggestion.boxId,
            seqNo: 0,
          },
        ],
        labels: [{ labelId: suggestion.labelId }],
        isolate: false,
      };
    },
  },

  render(create): VNode {
    return create(
      DocumentResultView, {
        props: {
          loading: this.loading,
          document: this.document,
          descriptors: this.descriptors,
          descriptor: this.currentDescriptor,
          ingest: this.result?.ingest,
          media: this.result?.data,
          fragments: this.fragments,
          scrollToFragmentId: this.scrollToFragmentId,
          suggestions: this.suggestions,
          extractedEntities: this.extractedEntities,
          entityTypes: this.entityTypes,
        },
        on: {
          load: this.load,
          changeIngestion: this.changeIngestion,
          saveFragment: this.onSaveFragment,
          deleteFragment: this.onDeleteFragment,
          revertFragment: this.onRevertFragment,
          createJob: this.createJob,
          saveSuggestionAction: this.saveSuggestionAction,
          removeAction: this.removeAction,
        },
      },
    );
  },
});
